import { stagger } from "animejs";

import { DOTS, ITALY_PATH, SARDEGNA_PATH, SICILIA_PATH } from "@/3-features/solutions/italy";
import { handles, prefersReducedMotion,useLoopInView } from "@/4-components/animations/anime";
import { GREEN, INK } from "@/5-shared/constants/palette";

import { BentoArt, bentoLoop } from "./BentoArt";

/**
 * Impatto, the public profile of `PublicProfile`, in the tile that leads to it.
 * The dec.energy address types itself, the page lands with its figures still empty,
 * the placeholders fill with measured production, and the plants take their places on
 * the map: the impact has coordinates. Same map data as the scene
 * (`3-features/solutions/italy`), no readable numbers, those are the page's job.
 */

const GREY = INK[300];
/** rows of the profile: a value bar and the caption under it */
const ROWS = [
  { val: "w-[68%]", cap: "w-[46%]" },
  { val: "w-[54%]", cap: "w-[38%]" },
  { val: "w-[62%]", cap: "w-[42%]" },
];

export function ProfileMini() {
  const ref = useLoopInView<HTMLDivElement>((root) => {
    const { one, all } = handles(root);
    const url = one<HTMLElement>(".pm-url");
    const rows = all<HTMLElement>(".pm-row");
    const bars = all<HTMLElement>(".pm-bar");
    const coast = all<SVGPathElement>(".pm-coast");
    const dots = all<SVGGElement>(".pm-dot");
    if (!url || rows.length === 0) return;

    if (prefersReducedMotion()) {
      // the punchline, held still: the page filled in, the plants on the map
      url.style.transform = "scaleX(1)";
      for (const el of [...rows, ...coast, ...dots]) {
        el.style.opacity = "1";
        el.style.transform = "none";
      }
      for (const b of bars) {
        b.style.backgroundColor = GREEN;
        b.style.transform = "scaleX(1)";
      }
      return;
    }

    return (
      bentoLoop()
        // 01, l'indirizzo su dec.energy si scrive da solo
        .add(url, { scaleX: [0, 1], duration: 600, ease: "outQuart" }, 300)
        // 02, la pagina atterra, con le cifre ancora vuote
        .add(rows, { opacity: [0, 1], translateY: [8, 0], duration: 400, delay: stagger(90), ease: "outQuad" }, 900)
        // 03, i segnaposto lasciano il posto alla produzione misurata
        .add(bars, { backgroundColor: [GREY, GREEN], scaleX: [0.55, 1], duration: 550, delay: stagger(110), ease: "out(3)" }, 1700)
        // 04, gli impianti prendono posto sulla mappa
        .add(coast, { fillOpacity: [0, 1], duration: 500 }, 2500)
        .add(dots, { opacity: [0, 1], scale: [0, 1], duration: 380, delay: stagger(55), ease: "outBack" }, 2800)
        // hold, then unwind to the start so the loop restart doesn't snap
        .add(dots, { opacity: [1, 0], scale: [1, 0], duration: 300, delay: stagger(35) }, 5900)
        .add(coast, { fillOpacity: [1, 0], duration: 400 }, 6200)
        .add(bars, { backgroundColor: [GREEN, GREY], scaleX: [1, 0.55], duration: 450, delay: stagger(60) }, 6400)
        .add(rows, { opacity: [1, 0], translateY: [0, 8], duration: 350, delay: stagger(60) }, 6900)
        .add(url, { scaleX: [1, 0], duration: 450 }, 7200)
    );
  });

  return (
    <BentoArt ref={ref} className="flex h-full items-end justify-center">
      <div className="w-full max-w-[21.25rem] rounded-2xl border border-zinc-200 bg-zinc-50/80 p-4">
        <div className="flex items-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <span key={i} className="h-1.5 w-1.5 rounded-full bg-zinc-300" />
          ))}
          <span className="pm-url ml-1.5 h-2 w-[72%] origin-left rounded-full bg-zinc-200" style={{ transform: "scaleX(0)" }} />
        </div>

        <div className="mt-3.5 flex items-start gap-3">
          <div className="flex-1 space-y-3">
            {ROWS.map((r, i) => (
              <div key={i} className="pm-row" style={{ opacity: 0 }}>
                <div className={`pm-bar h-2.5 origin-left rounded-sm ${r.val}`} style={{ backgroundColor: GREY, transform: "scaleX(0.55)" }} />
                <div className={`mt-1 h-1.5 rounded-sm bg-zinc-200 ${r.cap}`} />
              </div>
            ))}
          </div>

          <svg viewBox="0 0 54 94" className="h-[8rem] w-auto shrink-0" preserveAspectRatio="xMidYMid meet">
            {[ITALY_PATH, SICILIA_PATH, SARDEGNA_PATH].map((d, i) => (
              <path key={i} className="pm-coast" d={d} fill={GREY} stroke={INK[400]} strokeWidth="0.5" strokeLinejoin="round" style={{ fillOpacity: 0 }} />
            ))}
            {DOTS.map((p) => (
              <g key={p.label} className="pm-dot" style={{ opacity: 0, transformBox: "fill-box", transformOrigin: "center" }}>
                <circle cx={p.x} cy={p.y} r="2.6" fill={GREEN} fillOpacity="0.22" />
                <circle cx={p.x} cy={p.y} r="1.3" fill={GREEN} />
              </g>
            ))}
          </svg>
        </div>
      </div>
    </BentoArt>
  );
}
